import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  fetchSloIngestCurrent,
  fetchSloIngestTrend,
} from '../../api/client';
import {
  MiniLineChart,
  StatusGrid,
  ProjectionTable,
  Section,
  MetricsLegend,
  TenantIndicator,
  formatLatency,
  formatLatencyMinutes,
  formatRelativeMinutes,
} from './sloShared';

const POLL_INTERVAL_MS = 30000;

const STAGE3_LEGEND = [
  { term: 'avg', defn: 'Mean of latency_seconds — sum of all per-blob latencies divided by count.' },
  { term: 'p50 (median)', defn: 'Half of blobs are at or below this latency. The "typical" experience.' },
  { term: 'p95 (SLO)', defn: '95% of blobs are at or below this. The Silver-Unified SLO is p95 ≤ 600 s (10 min) with miss-rate < 5%.' },
  { term: 'p99', defn: '99% of blobs are at or below this. The "tail" — useful for outlier hunting.' },
  { term: 'Daily (no recent)', defn: 'Workload that emits ~1 blob per day around the daily collection run. Showing "no data" between runs is normal cadence, not a problem.' },
];

export default function IngestTab({ active, tenantId = '', tenantLookup = {} }) {
  const [current, setCurrent] = useState(null);
  const [trend, setTrend] = useState(null);
  const [displayedTenantId, setDisplayedTenantId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadCurrent = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchSloIngestCurrent({ tenantId });
      setCurrent(data);
      setDisplayedTenantId(tenantId);
      setError('');
    } catch (err) {
      console.error('[IngestTab] current fetch failed', err);
      setError(err?.message || 'Failed to load silver-unified ingest data.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId]);

  const loadTrend = useCallback(async () => {
    try {
      const data = await fetchSloIngestTrend({ days: 14 });
      setTrend(data);
    } catch (err) {
      console.error('[IngestTab] trend fetch failed', err);
    }
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    loadCurrent(false);
    loadTrend();
    const id = setInterval(() => loadCurrent(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active, loadCurrent, loadTrend]);

  const thresholdSec = current?.thresholdSec ?? trend?.thresholdSec ?? 600;

  const workloadCols = useMemo(() => [
    { key: 'samples',   label: 'Blobs',     align: 'right' },
    { key: 'missCount', label: 'Misses',    align: 'right' },
    { key: 'missRate',  label: 'Miss rate', align: 'right',
      fmt: (v) => v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%` },
    { key: 'avgSec',    label: 'avg',       align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p50Sec',    label: 'p50',       align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p95Sec',    label: 'p95 (SLO)', align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p99Sec',    label: 'p99',       align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'maxSec',    label: 'Max',       align: 'right',
      fmt: (v) => formatLatency(v) },
  ], []);

  return (
    <div>
      {error && (
        <div className="card mb-4 bg-red-900/20 border-red-700/40 text-red-200 text-sm">
          {error}
        </div>
      )}

      <Section
        title="Current ingest latency (last 1 hour)"
        subtitle={`silver-staging → Delta append. Goal: ${thresholdSec}s (10 min) per blob, miss-rate < 5%.`}
        right={
          <div className="flex items-center gap-3">
            <TenantIndicator
              selectedTenantId={tenantId}
              displayedTenantId={displayedTenantId}
              tenantLookup={tenantLookup}
              loading={loading}
            />
            {current && (
              <div className="text-xs text-gray-500">
                {loading ? 'refreshing…' : `updated ${formatRelativeMinutes(current.generatedUtc)}`}
              </div>
            )}
            <MetricsLegend items={STAGE3_LEGEND} />
          </div>
        }
      >
        <StatusGrid
          rows={current?.workloads || []}
          columns={workloadCols}
          emptyText={loading ? 'Loading…' : 'No ingest telemetry in the last hour.'}
        />
      </Section>

      <Section
        title="14-day p95 trend per workload"
        subtitle={`Daily p95 latency from SilverM365IngestLatency. Dashed line = ${thresholdSec}-second goal.`}
      >
        <TrendCharts trend={trend} thresholdSec={thresholdSec} />
      </Section>

      <Section
        title="Days-until-exceeded projection"
        subtitle="Linear regression over daily p95."
      >
        <ProjectionTable
          workloads={trend?.workloads || []}
          targetValue={thresholdSec / 60}
          targetUnit="min"
          currentField="currentP95"
          projectedField="projectedIn14d"
          valueFormatter={(v) => formatLatencyMinutes(v, 2)}
          slopeFormatter={(v) => v == null ? '—' : (Number(v) / 60).toFixed(3)}
        />
      </Section>

      <Section
        title="Top tenants by miss count"
        subtitle="Last hour. Drill-down for which tenants are dragging the workload p95."
      >
        <TopTenantsTable workloads={current?.workloads || []} tenantLookup={tenantLookup} />
      </Section>
    </div>
  );
}

function TrendCharts({ trend, thresholdSec }) {
  if (!trend || !trend.workloads || trend.workloads.length === 0) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">No trend data yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {trend.workloads.map((w) => {
        const series = (w.dailySeries || []).map((d) => ({ day: d.day, value: d.p95Sec }));
        return (
          <div key={w.workload} className="border border-gray-800 rounded-lg p-3 bg-gray-950/50">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-xs text-gray-300">{w.workload}</span>
              <span className="text-[11px] text-gray-500">
                {w.samples ?? 0} days obs · slope {Number(w.slope ?? 0).toFixed(2)}s/day
              </span>
            </div>
            <MiniLineChart series={series} threshold={thresholdSec} yLabel="s" height={70} />
          </div>
        );
      })}
    </div>
  );
}

function TopTenantsTable({ workloads, tenantLookup = {} }) {
  const rows = (workloads || []).filter((w) => Array.isArray(w.topTenants) && w.topTenants.length > 0);
  if (rows.length === 0) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">No tenant-level misses in the last hour.</div>;
  }
  const labelFor = (tid) => tenantLookup[tid] || null;
  return (
    <div className="space-y-3">
      {rows.map((w) => (
        <div key={w.workload} className="border border-gray-800 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs text-gray-300">{w.workload}</span>
            <span className="text-[11px] text-gray-500">{w.missCount} misses · {(w.missRate * 100).toFixed(1)}% miss rate</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left border-b border-gray-800/50">
                <th className="px-2 py-1 font-medium text-gray-500">Tenant</th>
                <th className="px-2 py-1 font-medium text-gray-500">Tenant ID</th>
                <th className="px-2 py-1 font-medium text-gray-500 text-right">Miss count</th>
              </tr>
            </thead>
            <tbody>
              {w.topTenants.map((t) => {
                const name = labelFor(t.tenantId);
                return (
                  <tr key={t.tenantId} className="border-b border-gray-800/30">
                    <td className="px-2 py-1 text-gray-200">
                      {name || <span className="text-gray-600 italic">unknown</span>}
                    </td>
                    <td className="px-2 py-1 font-mono text-[10px] text-gray-500">{t.tenantId}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{t.missCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
